import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../axiosSetup";
import "bootstrap/dist/css/bootstrap.min.css";
import CustomNavbar from "../CustomNavbar";
import Select from "react-select";
import cfg from "../../config";

const customStyles = {
  control: (styles) => ({
    ...styles,
    backgroundColor: "white", // Hintergrund bleibt weiß
    color: "black",
    borderColor: "#6c757d",
  }),
  singleValue: (styles) => ({
    ...styles,
    color: "black", // Ausgewählter Wert schwarz
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: "white", // Dropdown-Hintergrund bleibt weiß
  }),
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    backgroundColor: isSelected ? "#007bff" : isFocused ? "#e9ecef" : "white",
    color: "black",
  }),
};

const HaesCreate = () => {
  const [formData, setFormData] = useState({
    ID: "",
    haesNummer: "",
    besitzer: "",
    typ: "", // wird gesetzt, sobald die Kostuemtypen geladen sind
    childHaes: "0",
    leihHaes: "0", // Default-Wert für "Nein"
    laufbendel: "1", // Default-Wert für "Ja"
    letzterLaufbendel: "",
    memberID: "", // Default-Wert für "nicht vergeben"
    verliehenAn: "", // neues Feld "Verliehen an"
    verliehenAnID: "", // ID der Person, an die verliehen wurde
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  // Die Kostuemtypen stehen in tbl_costume_types und werden im Zunftrat
  // gepflegt. Frueher las diese Ansicht sie aus REACT_APP_COSTUME_TYPES —
  // eine Variable, die seit der Umstellung auf die Datenbank niemand mehr
  // setzt. Die Auswahlliste blieb dadurch leer und kein Eintrag liess sich
  // anlegen.
  const [kostuemTypen, setKostuemTypen] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    api.get("/api/costume-types", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const typen = r.data || [];
        setKostuemTypen(typen);
        // Vorauswahl erst jetzt moeglich — beim Bearbeiten nicht ueberschreiben.
        setFormData((f) => (f.typ ? f : { ...f, typ: typen[0]?.name || "" }));
      })
      .catch(() => setError(`Die ${cfg.costumeTerm}typen konnten nicht geladen werden.`));
  }, []);

  useEffect(() => {
    const fetchMembers = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        const response = await api.get("/api/all-member-zunftrat", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setMembers(response.data);
      } catch (err) {
        console.error("Fehler beim Abrufen der Mitglieder:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
  }, []);

  useEffect(() => {
    if (location.state && location.state.haesItem) {
      const haesItem = location.state.haesItem;
      console.log("Bearbeiten-Modus Daten:", haesItem);

      setFormData({
        ID: haesItem.ID,
        haesNummer: haesItem.number || "",
        besitzer: haesItem.owner?.name || "",
        typ: haesItem.typ || "",
        childHaes: haesItem.isKidsHaes ? "1" : "0",
        leihHaes: haesItem.isRentHaes ? "1" : "0",
        laufbendel: haesItem.hasTreadmills ? "1" : "0",
        letzterLaufbendel: haesItem.lastTreadmills || "",
        memberID: haesItem.owner?.memberID || "",
        verliehenAn: haesItem.awarded?.name || "",
        verliehenAnID: haesItem.awarded?.memberID || "",
      });
      setIsEditMode(true); // Schaltet in den Bearbeitungsmodus
    }
  }, [location.state]);

  const memberOptions = [
    { value: "", label: "nicht vergeben" },
    ...members.map((member) => ({
      value: member.ID,
      label: `${member.Vorname} ${member.Nachname}`,
    })),
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!formData.memberID) {
      setError(`${cfg.memberTerm} muss ausgewählt werden.`);
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem("authToken");
      if (isEditMode) {
        // Bearbeiten
        await api.put(`/api/edit-haes-zunftrat/${formData.haesNummer}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        navigate("/zr-haes", { state: { message: `${cfg.costumeTerm} erfolgreich bearbeitet!` } });
      } else {
        // Erstellen
        await api.post("/api/create-haes-zunftrat", formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        navigate("/zr-haes", { state: { message: `${cfg.costumeTerm} erfolgreich erstellt!` } });
      }
    } catch (err) {
      setError(`Fehler beim Speichern (${cfg.costumeTerm}): ` + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!formData.haesNummer) return;

    const confirmed = window.confirm(`Möchtest du diesen Eintrag wirklich löschen (${cfg.costumeTerm})?`);
    if (!confirmed) return;

    try {
      const token = localStorage.getItem("authToken");
      await api.delete(`/api/delete-haes-zunftrat/${formData.ID}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      navigate("/zr-haes", { state: { message: `${cfg.costumeTerm} erfolgreich gelöscht!` } });
    } catch (err) {
      setError(`Fehler beim Löschen (${cfg.costumeTerm}): ` + err.message);
    }
  };

  return (
    <div>
      <CustomNavbar />
      <div className="container mt-4">
        <h2 className="mb-4 text-center">{isEditMode ? `${cfg.costumeTerm} bearbeiten` : `${cfg.costumeTerm} erstellen`}</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label">Nummer</label>
            <input
              type="text"
              className="form-control"
              name="haesNummer"
              value={formData.haesNummer}
              onChange={handleChange}
              required
              
            />
          </div>

          {/* Mitglied Auswahl */}
          <div className="mb-3">
            <label htmlFor="memberID" className="form-label">{cfg.memberTerm}</label>
            <Select
              id="memberID"
              name="memberID"
              options={memberOptions}
              value={memberOptions.find(option => option.value === formData.memberID)}
              onChange={(selectedOption) => setFormData({ ...formData, memberID: selectedOption?.value || "" })}
              isSearchable
              placeholder={`${cfg.memberTerm} auswählen...`}
              styles={customStyles}
            />
          </div>

          {/* Verliehen an Auswahl */}
          <div className="mb-3">
            <label htmlFor="verliehenAn" className="form-label">Verliehen an</label>
            <Select
              id="verliehenAn"
              name="verliehenAn"
              options={memberOptions}
              value={memberOptions.find(option => option.value === formData.verliehenAnID)}
              onChange={(selectedOption) => setFormData({ ...formData, verliehenAnID: selectedOption?.value || "" })}
              isSearchable
              placeholder="Verliehen an auswählen..."
              styles={customStyles}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Typ</label>
            <select
              className="form-select"
              name="typ"
              value={formData.typ}
              onChange={handleChange}
              required
            >
              {kostuemTypen.map((t) => (
                <option key={t.id} value={t.name}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Kinder-{cfg.costumeTerm}</label>
            <select
              className="form-select"
              name="childHaes"
              value={formData.childHaes}
              onChange={handleChange}
              required
            >
              <option value="0">Nein</option>
              <option value="1">Ja</option>
            </select>
          </div>

          <div className="mb-3">
            <label className="form-label">Leihgabe</label>
            <select
              className="form-select"
              name="leihHaes"
              value={formData.leihHaes}
              onChange={handleChange}
              required
            >
              <option value="0">Nein</option>
              <option value="1">Ja</option>
            </select>
          </div>

          <div className="mb-3">
            <label className="form-label">Laufbendel</label>
            <select
              className="form-select"
              name="laufbendel"
              value={formData.laufbendel}
              onChange={handleChange}
              required
            >
              <option value="0">Nein</option>
              <option value="1">Ja</option>
            </select>
          </div>

          <div className="mb-3">
            <label className="form-label">Letzter Laufbendel</label>
            <input
              type="number"
              className="form-control"
              name="letzterLaufbendel"
              value={formData.letzterLaufbendel}
              onChange={handleChange}
            />
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          <button type="submit" className="btn btn-primary">
            {isEditMode ? "Änderungen speichern" : `${cfg.costumeTerm} erstellen`}
          </button>
          {isEditMode && (
            <button
              type="button"
              className="btn btn-danger ms-3"
              onClick={handleDelete}
            >
              {cfg.costumeTerm} löschen
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default HaesCreate;
